import React, { useContext, useEffect, useReducer, useState } from "react";
import { Panel } from "../../common/components/panel/panel";
import { TableV } from "../../common/components/table/tablev2";
import { ColumnsDefinitions } from "./ColumnDefinitions";
import { GetCompanyList, GetImportFiles, UploadCsv, CheckAssignedCompany, downloadFileFromUrl } from "../../services/ImportDetailService";
import { FileModal } from "../FileModal/FileModal";
import { Navbar, NavbarItem } from "../../common/components/navbar/navbar";
import { ProgressOverlay } from "../../common/components/progress-overlay/progress-overlay";
import { AdvancedFilter, FilterField, FilterFieldTypes, FilterValue } from "../../common/components/advanced-filter/advanced-filter";
import StandALoneContext from "../../common/components/context/StandAloneContext";
import { FileType, IsProduction, StandAloneActions, Status } from "../../models/StandAloneData";
import { FormatFullDate } from "../../common/formatting";
import Banner from "../../common/components/banner/banner";
import { ValidationText } from "@ceridianhcm/components";
import logger from '../../logger';
import './PayrollSummary.scss';


const defaultFilters: FilterValue[] = [];

const textResources = {
    tableDescription: "File(s) Import Status",
    tableNoDataText: "No Files Found",
    tableAddItemText: "",
};

const typeValues = [
    { id: '0', title: 'Pay' },
    { id: '1', title: "ROE" }
];

const statusValues = [
    { id: '0', title: 'Validating' },
    { id: '1', title: "Aggregating" },
    { id: '2', title: "Transmitting" },
    { id: '3', title: "Completed" },
    { id: '4', title: "Error" },
    { id: '5', title: "TransmissionError" }
];

const environmentValues = [
    { id: '1', title: 'Prod' },
    { id: '0', title: "Test" }
];

interface IReducerProperties {
    formValues: any | null,
    page: number,
    currentMenu: NavbarItem[],
    progressMessage: string,
    loading: boolean
};

interface IReducerAction {
    type: string,
    value?: any | null
};

function dataReducer(state: IReducerProperties, action: IReducerAction): IReducerProperties {
    let result: IReducerProperties = {
        formValues: state.formValues,
        page: state.page,
        currentMenu: state.currentMenu,
        progressMessage: state.progressMessage,
        loading: state.loading
    };

    switch (action.type) {
        case 'pageLoaded':
            result.loading = false;
            break;
        case 'toggleFilter':
            result.currentMenu = [...result.currentMenu];
            result.currentMenu[0].Active = !result.currentMenu[0].Active;
            result.loading = false;
            break;
        case 'exportpayrollsummary':
            logger.info('Export button click event called..');
            break;
        case 'default':
            break;
    }
    return result;
}



const PayrollSummary: React.FC<{ rowsPerPage?: any }> = ({
    rowsPerPage = 10,
}) => {

    const { state, dispatch: contextDispatch } = useContext(StandALoneContext);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [localRowsPerPage, setLocalRowsPerPage] = useState<number>(rowsPerPage);
    const [companyIds, setCompanyIds] = useState([]);
    const [companyNamesLst, setCompanyNames] = useState([]);
    const [importFilesData, setImportFilesData] = useState(state.importFilesData);
    const [error, setError] = useState(false);
    const [exportFlag, setExportFlag] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    const handleExportClick = async () => {
        setExportFlag(true);
    }

    useEffect(() => {
        if (exportFlag) {
            getImportFilesData();
        }
    }, [exportFlag]);


    const CreateExport = async (data) => {
        try {
            setExportFlag(false);
            const excludedColumns = ['importId', 'collectorId'];
            const response = data;
            const dataToExport = response?.output || [];

            if (dataToExport.length === 0) {
                logger.warn("No data available for export.");
                return;
            }

            const columns = [
                {
                    field: "companyName",
                    label: "Company Name",
                    cellTemplate: (rowData) => rowData.companyName,
                },
                {
                    field: "companyId",
                    label: "Company ID",
                    cellTemplate: (rowData) => rowData.companyId.slice(1),
                },
                {
                    field: "fileName",
                    label: "File Name",
                    cellTemplate: (rowData) => rowData.fileName,
                },
                {
                    field: "fileType",
                    label: "File Type",
                    cellTemplate: (rowData) => FileType[rowData.fileType],
                },
                {
                    field: "isProduction",
                    label: "Prod/Test",
                    cellTemplate: (rowData) => IsProduction[rowData.isProduction],
                },
                {
                    field: "status",
                    label: "Status",
                    cellTemplate: (rowData) => Status[rowData.status],
                },
                {
                    field: "createdOn",
                    label: "Created Date",
                    cellTemplate: (rowData) => FormatFullDate(rowData.createdOn),
                },
            ];

            const headers = columns
                .filter(col => !excludedColumns.includes(col.field))
                .map(col => col.label);

            const csvRows = [];
            csvRows.push(headers.join(','));

            dataToExport.forEach((row) => {
                const rowValues = columns
                    .filter(col => !excludedColumns.includes(col.field))
                    .map(col => {
                        let cellValue = col.cellTemplate(row);
                        // Escape quotes for CSV
                        if (typeof cellValue === 'string') {
                            cellValue = `"${cellValue.replace(/"/g, '""')}"`;
                        }
                        return cellValue;
                    });
                csvRows.push(rowValues.join(','));
            });

            const now = new Date();
            const formattedDate = now.toISOString().replace(/[:.-]/g, '').slice(0, 15);
            const csvfileName = `PayrollProcessing_${formattedDate}.csv`;
            const screenName = "Payroll Processing";
            const csvContent = csvRows.join('\n');

            try {
                const uplaodResponse = await UploadCsv(csvfileName, csvContent, screenName);
                const blobUrl = uplaodResponse.data;

                if (blobUrl) {
                    try {
                        downloadFileFromUrl(blobUrl, csvfileName);
                    } catch (e) {
                        logger.error('Invalid download URL');
                    }
                } else {
                    logger.error('No blob URL returned from server.');
                }
            } catch (error) {
                setError(true);
                logger.error('Upload failed:', error);
            }


        } catch (error) {
            setError(true);
            logger.error("Error fetching or exporting data:", error);
        }
    };


    const filterMenuItem = new NavbarItem({
        active: false,
        title: 'Filter',
        icon: 'filter',
        onClick: async () => {
            try {
                const result = await CheckAssignedCompany();
                const isAssigned = !result;
                const isFilteropen = reducerState.currentMenu[0].Active;
                if (isFilteropen) {
                    setMessage(null);
                } else {
                    setMessage(isAssigned ? "Your access to view company data is not configured. Please contact support to review and setup your access privileges." : null);
                }
                dispatch({ type: 'toggleFilter' })
            } catch (error) {
                logger.error("CheckAssignedCompany failed: Unable to verify company assignment Details", error);
            }
        }
    });

    const exportMenuItem = new NavbarItem({
        active: false,
        title: 'Export',
        icon: 'export',
        onClick: handleExportClick
    });

    const [reducerState, dispatch] = useReducer(dataReducer, {
        formValues: null,
        page: state.serverPage,
        progressMessage: "Loading...",
        loading: true,
        currentMenu: [filterMenuItem]
    });

    let FilterFields: FilterField[] = [
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'companyName',
            title: 'Company Name',
            options: companyNamesLst
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'companyId',
            title: 'Company ID ',
            options: companyIds
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'fileType',
            title: 'File Type',
            options: typeValues
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'isProduction',
            title: 'Prod/Test',
            options: environmentValues
        }),
        new FilterField({
            type: FilterFieldTypes.multiple_select,
            name: 'status',
            title: 'Status',
            options: statusValues
        })
    ];

    const getCompanyList = async () => {
        let companyIDS = new Array();
        let companyNamesList = new Array();
        try {
            const data = await GetCompanyList();
            if (data && data.length > 0) {
                data.forEach(x => {
                    companyIDS.push({ id: x.companyId, title: x.companyId.slice(1) });
                    companyNamesList.push({ id: x.companyId, title: x.companyId.slice(1) + ' ' + x.companyName });
                });
            }
            setCompanyIds(companyIDS);
            setCompanyNames(companyNamesList);
        } catch (error) {
            setError(true);
            return new Error(error)
        }
    };

    async function getImportFilesData(pageSize?: number) {
        try {
            const fileTypeFilter = state.filterData.fileType ?? ['0', '1'];
            if (exportFlag) {
                const inputFilters = {
                    page: state.serverPage,
                    pageSize: 0,
                    companyName: state.filterData.companyName,
                    companyId: state.filterData.companyId,
                    fileType: fileTypeFilter,
                    isProduction: state.filterData.isProduction,
                    status: state.filterData.status
                };
                const response = await GetImportFiles(inputFilters);
                CreateExport(response);
            }
                else {
                const inputFilters = {
                    page: state.serverPage,
                    pageSize: pageSize ?? localRowsPerPage,
                    companyName: state.filterData.companyName,
                    companyId: state.filterData.companyId,
                    fileType: fileTypeFilter,
                    isProduction: state.filterData.isProduction,
                    status: state.filterData.status
                };
                const response = await GetImportFiles(inputFilters);
                setImportFilesData(response);
                setLoading(false);
            }
        } catch (error) {
            setError(true);
            return new Error(error)
        }
    };

    useEffect(() => {
        getCompanyList();
        dispatch({
            type: 'pageLoaded',
            value: null
        });
    }, []);

    useEffect(() => {
        if (state.serverPageChange !== null || state.serverPage !== null || state.applyFilterClick !== null) {
            getImportFilesData();
        }
    }, [state.serverPageChange, state.serverPage, state.applyFilterClick]);

    const goToNextPage = () => {
        contextDispatch({
            type: StandAloneActions.SET_MAIN_PAGE,
            payload: { ...state },
        });
    };

    const applyFilters = async (filters: FilterValue[] | null) => {
        let filterValues: any = {};
        if (filters !== null) {
            filters.forEach((filter) => {
                switch (filter.FieldName) {
                    case 'companyId':
                        filterValues["companyId"] = filter.Value as string[];
                        break;
                    case 'companyName':
                        filterValues["companyName"] = filter.Value as string[];
                        break;
                    case 'fileType':
                        filterValues["fileType"] = filter.Value as string[];
                        break;
                    case 'isProduction':
                        filterValues["isProduction"] = filter.Value as string[];
                        break;
                    case 'status':
                        filterValues["status"] = filter.Value as string[];
                        break;
                }
            });
        }
        contextDispatch({
            type: StandAloneActions.SET_FILTER_DATA,
            payload: {
                pageIdx: 0,
                filterValues: filterValues
            }
        });
    }

    return (
        <>

            {error && <Banner
                errorStatus="Error: "
                errorDescription="An internal server error occurred, check with administrator." ></Banner>}
            <ProgressOverlay message={reducerState.progressMessage} visible={reducerState.loading} testId="progressOverlay" />
            <Panel fullWidth={true}>
                <Panel.Block>
                    <Navbar
                        startItems={reducerState.currentMenu}
                        endItems={[exportMenuItem]}
                        testId="primaryNavBar"
                    />
                </Panel.Block>
                {message &&
                    <Panel.Block>
                        <ValidationText variant="error" id="Validation-text" testId="Validation-text">{message}</ValidationText>
                    </Panel.Block>}
                <Panel.Block visible={reducerState.currentMenu[0].Active} focused={true}>
                    <AdvancedFilter
                        filters={FilterFields}
                        defaultFilters={defaultFilters}
                        applyFilters={(filters) => { applyFilters(filters); }}
                        testId="primaryAdvancedFilter"
                        requiresAtLeastOne={true}
                    />

                </Panel.Block>
                {/* <Panel.Block>
                    <Table
                        testId={"canada-standalone-ui"}
                        columns={ColumnsDefinitions(setShowModal)}
                        isLoading={loading}
                        dataLength={importFilesData?.count}
                        data={importFilesData?.output}
                        dataKey={"Canada"}
                        description={textResources.tableDescription}
                        noDataText={textResources.tableNoDataText}
                        addItemText={textResources.tableAddItemText}
                        pageIndex={state.serverPage}
                        getNextPageDetailsFromAPI={goToNextPage}
                        rowsPerPage={rowsPerPage}
                        tableHeight="83vh"
                    />
                </Panel.Block> */}

                <Panel.Block>
                    <TableV
                        testId={"canada-standalone-ui"}
                        columns={ColumnsDefinitions(setShowModal)}
                        isLoading={loading}
                        dataLength={importFilesData?.count}
                        data={importFilesData?.output}
                        dataKey={"Canada"}
                        description={textResources.tableDescription}
                        noDataText={textResources.tableNoDataText}
                        addItemText={textResources.tableAddItemText}
                        pageIndex={state.serverPage}
                        getNextPageDetailsFromAPI={goToNextPage}
                        rowsPerPage={localRowsPerPage}
                        onRowsPerPageChange={(newSize) => { setLocalRowsPerPage(newSize); getImportFilesData(newSize); }}
                        rowsPerPageOptions={[10,25,50,100]}
                        serverSide={true}
                        tableHeight="83vh"
                    />
                </Panel.Block>

            </Panel>
            <FileModal showModal={showModal} onCancel={() => setShowModal(false)} />
        </>
    );
};

export default PayrollSummary;